/**
 * OKS-503 — EMV (Earned Media Value) money formatting for the brand EMV
 * dashboard. EMV is stored and transported as integer **cents** (BIGINT
 * `emv_cents`); the FE divides by 100 and renders USD. There is NO
 * client-side EMV recomputation — these helpers only format the
 * server-computed cents (PRD §6 monetary integrity).
 *
 *   formatEmvUsd(129000)      → "$1,290.00"   (full — tooltips + table cells)
 *   formatEmvCompact(129000)  → "$1.3K"       (compact — KPI chips, tight UI)
 *   formatEmvCompact(2400000_00) → "$2.4M"
 *   formatEmvDeltaUsd(50000)  → "+$500.00"    (signed — day-over-day)
 *   formatEmvDeltaUsd(-50000) → "-$500.00"
 *   formatEmvDeltaUsd(null)   → "—"           (first-day edge)
 *
 * The full form is locale-aware (thousands separator + 2 decimals via
 * `Intl.NumberFormat` currency style). The compact form mirrors the
 * `formatCompactNumber` posture (locale-independent K/M/B) prefixed with
 * the `$` sign so KPI chips stay narrow.
 */

/** Em-dash placeholder rendered when a value is absent (first-day Δ). */
export const EMV_EMPTY = "—"

/** Cents → exact dollars as a Number (e.g. 129000 → 1290.0). */
export function emvCentsToDollars(cents: number): number {
  return cents / 100
}

/**
 * Full USD form with thousands grouping + 2 decimals. Locale-aware.
 *
 * @param cents integer EMV cents (server-computed)
 * @param locale BCP-47 tag (defaults to the active i18n language at call site)
 */
export function formatEmvUsd(cents: number, locale?: string): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(emvCentsToDollars(cents))
}

const COMPACT_TIERS = [
  { threshold: 1_000_000_000, suffix: "B" },
  { threshold: 1_000_000, suffix: "M" },
  { threshold: 1_000, suffix: "K" },
]

/**
 * Compact `$1.3K` / `$2.4M` form for KPI chips + tight cells. Operates on
 * the dollar magnitude (cents / 100). Below $1,000 falls back to the full
 * 2-decimal form so small values stay precise.
 */
export function formatEmvCompact(cents: number, locale?: string): string {
  const dollars = emvCentsToDollars(cents)
  const abs = Math.abs(dollars)

  if (abs < 1_000) {
    return formatEmvUsd(cents, locale)
  }

  const tier = COMPACT_TIERS.find((t) => abs >= t.threshold)!
  const rounded = Math.round((dollars / tier.threshold) * 10) / 10
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)

  return `$${text}${tier.suffix}`
}

/**
 * Signed day-over-day delta in full USD. `null` (the one-snapshot-day
 * "first day" edge) renders the em-dash. Direction is carried by the
 * leading sign here; callers ALSO render an ▲/▼ glyph so color is never
 * the only signal (UX Spec §6 a11y).
 *
 * @param cents signed EMV-cents delta, or `null` for first-day
 */
export function formatEmvDeltaUsd(
  cents: number | null,
  locale?: string,
): string {
  if (cents === null) return EMV_EMPTY

  const sign = cents > 0 ? "+" : cents < 0 ? "-" : ""
  return `${sign}${formatEmvUsd(Math.abs(cents), locale)}`
}

/** Direction of a delta for arrow/aria selection. `null` → "flat". */
export function emvDeltaDirection(
  cents: number | null,
): "up" | "down" | "flat" {
  if (cents === null || cents === 0) return "flat"
  return cents > 0 ? "up" : "down"
}
